import Vue from "vue"
import apollo from "@/apollo.js"
import gql from "graphql-tag"

let chatSessionObserver = null
let chatMessageObserver = null
let chatSessionSubscribedId = null
let chatMessagesSubscribedId = null
let chatSessionRetryTimer = null
let chatMessagesRetryTimer = null

const CHAT_WS_RETRY_MS = 1500

// Debounced refetch of messages when session updates (fallback when message subscription misses events)
const SESSION_REFETCH_DEBOUNCE_MS = 400
const sessionRefetchTimeouts = {}
function scheduleRefetchMessagesForSession(sessionId) {
  if (sessionRefetchTimeouts[sessionId] != null) {
    window.clearTimeout(sessionRefetchTimeouts[sessionId])
  }
  sessionRefetchTimeouts[sessionId] = window.setTimeout(() => {
    sessionRefetchTimeouts[sessionId] = null
    const store = Vue.store
    if (store && store.state.chat.currentSession && store.state.chat.currentSession.id === sessionId) {
      console.log("[Chat WS] session updated: merging messages for session", sessionId)
      store.dispatch("mergeMessagesFromSessionRefetch", sessionId)
    }
  }, SESSION_REFETCH_DEBOUNCE_MS)
}

function clearChatSessionRetry() {
  if (chatSessionRetryTimer != null) {
    window.clearTimeout(chatSessionRetryTimer)
    chatSessionRetryTimer = null
  }
}

function clearChatMessagesRetry() {
  if (chatMessagesRetryTimer != null) {
    window.clearTimeout(chatMessagesRetryTimer)
    chatMessagesRetryTimer = null
  }
}

function scheduleChatSessionRetry(sessionId) {
  clearChatSessionRetry()
  chatSessionRetryTimer = window.setTimeout(() => {
    chatSessionRetryTimer = null
    if (chatSessionSubscribedId === sessionId) {
      console.log("[Chat WS] subscribeToChatSession RETRY", { sessionId: sessionId })
      subscribeToChatSession(sessionId)
    }
  }, CHAT_WS_RETRY_MS)
}

function scheduleChatMessagesRetry(sessionId) {
  clearChatMessagesRetry()
  chatMessagesRetryTimer = window.setTimeout(() => {
    chatMessagesRetryTimer = null
    if (chatMessagesSubscribedId === sessionId) {
      console.log("[Chat WS] subscribeToChatMessages RETRY", { sessionId: sessionId })
      subscribeToChatMessages(sessionId)
    }
  }, CHAT_WS_RETRY_MS)
}

export const getAllChatSessions = async (data) => {
  const response = await apollo.query({
    query: gql(`query getAllChatSession { getAllChatSessions { chatSessions {id title createdAt updatedAt user {id email displayName}} error message lowLevelErrorMessage}}`),
  })
  return response.data.getAllChatSessions.chatSessions
}

export const getChatSession = async (id) => {
  const response = await apollo.query({
    query: gql(`query getChatSession($id: Int!) { getChatSession(id: $id) { chatSession {id title createdAt updatedAt user {id email displayName}} error message lowLevelErrorMessage}}`),
    variables: { id: id },
  })
  return response.data.getChatSession.chatSession
}

export const createChatSession = async (data) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation createChatSession($title: String!) {
                createChatSession(title: $title) { chatSession { id title createdAt updatedAt user {id email displayName} messages {id text display author {id email displayName} data createdAt error, upVoted, downVoted} } error message lowLevelErrorMessage }
            }`),
    variables: { title: data.title },
  })
  return response.data.createChatSession.chatSession
}
export const deleteChatSession = async (id) => {
  const sessionId = typeof id === "number" ? id : parseInt(id, 10)
  if (Number.isNaN(sessionId)) {
    throw new Error("Invalid session id")
  }
  const response = await apollo.mutate({
    mutation: gql(`
            mutation deleteChatSession($id: Int!) {
                deleteChatSession(id: $id) { chatSession { id title createdAt updatedAt } error message lowLevelErrorMessage }
            }`),
    variables: { id: sessionId },
  })
  const result = response.data && response.data.deleteChatSession
  if (!result) {
    const err = response.errors && response.errors[0]
    const msg = (err && (err.message || err.debugMessage)) || (response.error && response.error.message) || "No response from server"
    throw new Error(msg)
  }
  if (result.error) {
    const err = new Error(result.message || "Unable to delete chat session")
    err.response = result
    throw err
  }
  return result.chatSession || null
}

export const updateChatSession = async (id, title) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation updateChatSession($id: Int!, $title: String!) {
                updateChatSession(id: $id, title: $title) { chatSession { id title createdAt updatedAt user { id email displayName } } error message lowLevelErrorMessage }
            }`),
    variables: { id, title },
  })
  const result = response.data.updateChatSession
  if (result && result.error) {
    const err = new Error(result.message || "Unable to update chat session")
    err.response = result
    throw err
  }
  return result.chatSession
}
export const createChatMessage = async (text, sessionId) => {
  console.log("[Chat] createChatMessage SEND", { sessionId, textLength: text && text.length })
  const response = await apollo.mutate({
    mutation: gql(`
            mutation createChatMessage($text: String!, $sessionId: Int!) {
                createChatMessage(text: $text, sessionId: $sessionId) { chatMessage {id text display statusHistory author {id email displayName} data createdAt error} error message lowLevelErrorMessage }
            }`),
    variables: { text: text, sessionId: sessionId },
  })
  const result = response.data.createChatMessage
  if (result && result.error) {
    const err = new Error(result.message || "Unable to send message")
    err.response = result
    throw err
  }
  console.log("[Chat] createChatMessage SUCCESS", { sessionId, messageId: result.chatMessage && result.chatMessage.id })
  return result.chatMessage
}
export const deleteChatMessage = async (id) => {}
export const updateChatMessage = async (id, data) => {}
export const createCaseTask = async (data) => {}

export const subscribeToChatSession = async (id) => {
  console.log("[Chat WS] subscribeToChatSession CONNECT", { sessionId: id })
  clearChatSessionRetry()
  if (chatSessionObserver) {
    try {
      chatSessionObserver.unsubscribe()
    } catch (e) {
      console.warn("[Chat WS] subscribeToChatSession prior unsubscribe failed", e)
    }
    chatSessionObserver = null
  }
  chatSessionSubscribedId = id
  const response = await apollo.subscribe({ query: gql(`subscription chatSessionUpdated($id: Int!) { chatSessionUpdated(id: $id) { id title createdAt updatedAt user {id email displayName}}}`), variables: { id: id } }).subscribe({
    async next(response) {
      const store = Vue.store
      const updatedSession = response.data.chatSessionUpdated
      // Only process if this update is for the session we subscribed to (backend may send all session updates to all clients)
      if (!updatedSession || updatedSession.id !== id) {
        if (updatedSession && updatedSession.id !== id) {
          console.log("[Chat WS] subscribeToChatSession RECEIVE ignore (different session)", { subscribedId: id, updateSessionId: updatedSession.id })
        }
        return
      }
      if (store.state.chat.currentSession && store.state.chat.currentSession.id === id) {
        const merged = Object.assign({}, store.state.chat.currentSession, updatedSession)
        store.commit("storeSession", merged)
        scheduleRefetchMessagesForSession(id)
      }
    },
    async error(err) {
      console.log("[Chat WS] subscribeToChatSession ERROR", { sessionId: id, error: err && err.message })
      if (chatSessionObserver) {
        try {
          chatSessionObserver.unsubscribe()
        } catch (e) { /* ignore */ }
        chatSessionObserver = null
      }
      scheduleChatSessionRetry(id)
    },
    complete() {
      console.log("[Chat WS] subscribeToChatSession COMPLETE", { sessionId: id })
      if (chatSessionObserver) {
        try {
          chatSessionObserver.unsubscribe()
        } catch (e) { /* ignore */ }
        chatSessionObserver = null
      }
      scheduleChatSessionRetry(id)
    },
  })
  chatSessionObserver = response
  console.log("[Chat WS] subscribeToChatSession SUBSCRIBED", { sessionId: id })
  return response
}
export const unsubscribeFromChatSession = async (id) => {
  if (sessionRefetchTimeouts[id] != null) {
    window.clearTimeout(sessionRefetchTimeouts[id])
    sessionRefetchTimeouts[id] = null
  }
  clearChatSessionRetry()
  if (chatSessionSubscribedId === id || id == null) {
    chatSessionSubscribedId = null
  }
  if (chatSessionObserver) {
    chatSessionObserver.unsubscribe()
    console.log("[Chat WS] unsubscribeFromChatSession DISCONNECT", { sessionId: id })
    chatSessionObserver = null
  } else {
    console.log("[Chat WS] unsubscribeFromChatSession (no active subscription)", { sessionId: id })
  }
}

export const subscribeToChatMessages = async (id) => {
  console.log("[Chat WS] subscribeToChatMessages CONNECT", { sessionId: id })
  clearChatMessagesRetry()
  if (chatMessageObserver) {
    try {
      chatMessageObserver.unsubscribe()
    } catch (e) {
      console.warn("[Chat WS] subscribeToChatMessages prior unsubscribe failed", e)
    }
    chatMessageObserver = null
  }
  chatMessagesSubscribedId = id
  const response = await apollo.subscribe({ query: gql(`subscription chatMessageSubscription($sessionId: Int!) { chatMessageSubscription(sessionId: $sessionId) { eventType chatMessage {id text display statusHistory author {id email displayName isBot} data meta createdAt updatedAt error}}}`), variables: { sessionId: id } }).subscribe({
    next(response) {
      const store = Vue.store
      const chatMessage = response.data.chatMessageSubscription.chatMessage
      const eventType = response.data.chatMessageSubscription.eventType
      console.log("[Chat WS] subscribeToChatMessages RECEIVE", { sessionId: id, eventType, messageId: chatMessage && chatMessage.id })

      switch (eventType) {
        case "MESSAGE_CREATED":
          store.dispatch("addMessage", chatMessage)
          break
        case "MESSAGE_UPDATED":
          store.dispatch("updateMessage", chatMessage)
          break
        case "MESSAGE_DELETED":
          store.dispatch("deleteMessage", chatMessage)
          break
        default:
          console.log("[Chat WS] subscribeToChatMessages unknown event", { sessionId: id, eventType })
      }
    },
    error(err) {
      console.log("[Chat WS] subscribeToChatMessages ERROR", { sessionId: id, error: err && err.message })
      if (chatMessageObserver) {
        try {
          chatMessageObserver.unsubscribe()
        } catch (e) { /* ignore */ }
        chatMessageObserver = null
      }
      scheduleChatMessagesRetry(id)
    },
    complete() {
      console.log("[Chat WS] subscribeToChatMessages COMPLETE", { sessionId: id })
      if (chatMessageObserver) {
        try {
          chatMessageObserver.unsubscribe()
        } catch (e) { /* ignore */ }
        chatMessageObserver = null
      }
      scheduleChatMessagesRetry(id)
    },
  })
  console.log("[Chat WS] subscribeToChatMessages SUBSCRIBED", { sessionId: id })
  chatMessageObserver = response
  return response
}

export const unsubscribeFromChatMessages = async (id) => {
  clearChatMessagesRetry()
  if (chatMessagesSubscribedId === id || id == null) {
    chatMessagesSubscribedId = null
  }
  if (chatMessageObserver) {
    chatMessageObserver.unsubscribe()
    console.log("[Chat WS] unsubscribeFromChatMessages DISCONNECT", { sessionId: id })
    chatMessageObserver = null
  } else {
    console.log("[Chat WS] unsubscribeFromChatMessages (no active subscription)", { sessionId: id })
  }
}

/** Re-bind chat GraphQL subscriptions after websocket reconnect and catch up missed status updates. */
export const reconnectChatSubscriptions = async (sessionId) => {
  if (sessionId == null || sessionId === undefined || sessionId === "") {
    return false
  }
  const id = typeof sessionId === "number" ? sessionId : parseInt(sessionId, 10)
  if (Number.isNaN(id)) {
    return false
  }
  console.log("[Chat WS] reconnectChatSubscriptions", { sessionId: id })
  await unsubscribeFromChatSession(id)
  await unsubscribeFromChatMessages(id)
  await subscribeToChatSession(id)
  await subscribeToChatMessages(id)
  const store = Vue.store
  if (store && store.state.chat.currentSession && store.state.chat.currentSession.id === id) {
    await store.dispatch("mergeMessagesFromSessionRefetch", id)
  }
  return true
}

export const getChatMessagesForSession = async (sessionId) => {
  const response = await apollo.query({
    query: gql(`query getChatMessagesForSession($sessionId: Int!) { getChatMessagesForSession(sessionId: $sessionId) {chatMessages {id text display statusHistory author {id email displayName isBot} meta data createdAt error upVoted downVoted} error message lowLevelErrorMessage}}`),
    variables: { sessionId: sessionId },
  })
  console.log("getChatMessagesForSession", response.data.getChatMessagesForSession)
  return response.data.getChatMessagesForSession
}

export const upVoteMessage = async (id) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation upVoteChatMessage($id: Int!) {
                upVoteChatMessage(id: $id) { chatMessage {id text display statusHistory author {id email displayName, isBot} data createdAt error, upVoted, downVoted} error message lowLevelErrorMessage }
            }`),
    variables: { id: id },
  })
  return response.data.upVoteChatMessage.chatMessage
}
export const downVoteMessage = async (id) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation downVoteChatMessage($id: Int!) {
                downVoteChatMessage(id: $id) { chatMessage {id text display statusHistory author {id email displayName, isBot} data createdAt error, upVoted, downVoted} error message lowLevelErrorMessage }
            }`),
    variables: { id: id },
  })
  return response.data.downVoteChatMessage.chatMessage
}

export const addChatMessageStatus = async (id, status) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation addChatMessageStatus($id: Int!, $status: String!) {
                addChatMessageStatus(id: $id, status: $status) { chatMessage {id text display statusHistory author {id email displayName isBot} data createdAt error} error message lowLevelErrorMessage }
            }`),
    variables: { id, status },
  })
  const result = response.data.addChatMessageStatus
  if (result && result.error) {
    const msg = result.lowLevelErrorMessage || result.message || "Unable to update message status"
    console.error("[addChatMessageStatus] API error:", { id, status, result })
    const err = new Error(msg)
    err.response = result
    throw err
  }
  return result.chatMessage
}
